/**
 * Profile "Invite friends" card — displays the user's referral code + stats,
 * an optional QR code (shareable on social), and a single share button that
 * opens Telegram's share-URL dialog.
 *
 * Inviting grants no reward — the call to action is purely "help Waylo grow."
 */
import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useT } from '../lib/i18n';
import { useSession } from '../lib/session';
import { Card, CardHint, CardTitle } from './Card';
import { getWebApp, hapticSuccess, hapticTap } from '../lib/telegram';

const BOT_USERNAME = (import.meta.env.VITE_BOT_USERNAME as string | undefined) ?? 'WayloRide_bot';

export function InviteCard(): JSX.Element | null {
  const { me } = useSession();
  const { t } = useT();
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  if (!me?.referralCode) return null;

  // Telegram deep-link: opens the bot with start_param=ref_<CODE>.
  const inviteUrl = `https://t.me/${BOT_USERNAME}?start=ref_${me.referralCode}`;
  const shareText = t('refer.shareText', { code: me.referralCode, url: inviteUrl });

  const share = (): void => {
    const tg = getWebApp();
    // Telegram exposes `t.me/share/url` for in-app share sheet.
    const shareUrl =
      `https://t.me/share/url?url=${encodeURIComponent(inviteUrl)}` +
      `&text=${encodeURIComponent(shareText)}`;
    if (tg?.openTelegramLink) {
      tg.openTelegramLink(shareUrl);
    } else if (navigator.share) {
      void navigator.share({ text: shareText, url: inviteUrl });
    } else {
      window.open(shareUrl, '_blank');
    }
    hapticSuccess();
  };

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(me.referralCode!);
      hapticSuccess();
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — ignore */
    }
  };

  const toggleQr = (): void => {
    hapticTap();
    setShowQr((v) => !v);
  };

  return (
    <Card className="space-y-3">
      <CardTitle>🤝 {t('refer.title')}</CardTitle>
      <CardHint>{t('refer.hint')}</CardHint>

      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1 rounded-input bg-tg-bg-secondary p-3">
          <p className="text-xs text-tg-hint">{t('refer.code')}</p>
          {/* Theme-aware text — was text-brand-navy (invisible in dark mode). */}
          <p className="font-mono text-lg font-bold text-tg-text">{me.referralCode}</p>
        </div>
        <button
          type="button"
          onClick={copy}
          aria-label={t('refer.copied')}
          className="rounded-input bg-tg-bg-secondary px-3 py-3 text-sm font-medium text-tg-text"
        >
          {copied ? '✓' : '📋'}
        </button>
      </div>

      <p className="text-xs text-tg-hint">
        {t('refer.stats', {
          count: me.referralsCount,
          rewarded: me.referralsRewarded,
        })}
      </p>

      {showQr && (
        <div className="flex flex-col items-center gap-2 pt-1">
          {/* QR needs fixed high-contrast colours to stay scannable in any
              theme — black on white is intentional, not theme styling. */}
          <div className="rounded-card bg-white p-4">
            <QRCodeSVG value={inviteUrl} size={180} level="M" fgColor="#000000" bgColor="#ffffff" />
          </div>
          <p className="text-center text-xs text-tg-hint">{t('refer.qrCaption')}</p>
          <p className="text-center text-xs text-tg-hint">{t('common.madeByTriardor')}</p>
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={toggleQr}
          className="flex-1 rounded-input bg-tg-bg-secondary py-3 text-sm font-medium text-tg-text"
        >
          {showQr ? t('refer.hideQr') : t('refer.showQr')}
        </button>
        <button
          type="button"
          onClick={share}
          className="flex-1 rounded-input bg-brand-green py-3 text-sm font-semibold text-white"
        >
          {t('refer.share')}
        </button>
      </div>
    </Card>
  );
}
